// Funciones de acceso a la API de categorías y subcategorías con caché local SWR
// No manipulan el DOM, solo retornan datos

import { apiConfig } from './api-config.js';

const ONE_HOUR = 60 * 60 * 1000;
const ONE_DAY = 24 * ONE_HOUR;

function now() { return Date.now(); }

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj;
  } catch { return null; }
}

function writeCache(key, data, ttlMs) {
  try {
    const obj = { data, ts: now(), ttl: ttlMs };
    localStorage.setItem(key, JSON.stringify(obj));
  } catch { /* quota full or private mode */ }
}

function isFresh(entry) {
  if (!entry || !entry.ttl || !entry.ts) return false;
  return (now() - entry.ts) < entry.ttl;
}

// SWR helper: devuelve data del caché si existe (aunque esté stale) y revalida en background
async function swrFetch({ key, url, ttl = ONE_DAY, parser = (d)=>d, onUpdate }) {
  const cached = readCache(key);
  if (cached && cached.data) {
    // Revalidar en segundo plano
    (async () => {
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (res.ok) {
          const json = await res.json();
          const data = parser(json);
          writeCache(key, data, ttl);
          if (typeof onUpdate === 'function') onUpdate(data);
        }
      } catch {}
    })();
    // Retornar inmediatamente (fresh o stale)
    return cached.data;
  }
  // Sin caché: fetch normal
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`No se pudo obtener recurso: ${url}`);
  const json = await res.json();
  const data = parser(json);
  writeCache(key, data, ttl);
  return data;
}

// Obtener todas las categorías principales (SWR)
export async function fetchCategories() {
  const url = `${apiConfig.baseURL}/categories`;
  const key = 'pkya:categories:v1';
  return swrFetch({
    key,
    url,
    ttl: ONE_DAY,
    parser: (data) => (data?.data?.categories ? data.data.categories : [])
  });
}

// Obtener todas las categorías (lista plana) (SWR)
export async function fetchAllCategoriesRaw() {
  const url = `${apiConfig.baseURL}/categories?include_subcategories=true`;
  const key = 'pkya:categories_raw:v1';
  return swrFetch({
    key,
    url,
    ttl: ONE_DAY,
    parser: (data) => (data?.data?.categories ? data.data.categories : [])
  });
}

// Obtener subcategorías de una categoría por slug (SWR por 1 hora)
export async function fetchSubcategories(categorySlug) {
  const url = `${apiConfig.baseURL}/categories/${categorySlug}`;
  const key = `pkya:subcategories:${categorySlug}:v1`;
  return swrFetch({
    key,
    url,
    ttl: ONE_HOUR,
    parser: (data) => (data?.data?.category?.subcategories ? data.data.category.subcategories : [])
  });
}
